import { useMemo, useState } from 'react';
import styled from 'styled-components';

import { Flex, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import ColorSelector from '@/components/Common/ColorSelector';
import Input from '@/components/Inputs/Input';
import { useModalContext } from '@/components/Modal/Modal';
import Heading from '@/components/Typography/Heading';
import useStates from '@/hooks/pure/useStates';
import { useToast } from '@/stores/pure/ToastStore';
import useTagStore from '@/stores/TagStore';
import { modalStyle } from '@/styles/style';

import { validateTagNameProperty } from './tagInputValidator';

type Props = {
  onCreateTagSuccess: () => void;
};

const CreateTagModal = (props: Props) => {
  const tagMap = useTagStore((state) => state.tagMap);
  const addTag = useTagStore((state) => state.addTag);
  const addToast = useToast();
  const { closeModal } = useModalContext();

  const rootTagNode = useMemo(() => {
    return Object.values(tagMap).find((tag) => tag.level === 0);
  }, [tagMap]);

  const { state, setValue } = useStates({
    color: 1,
    name: '',
  });

  const [isLoading, setIsLoading] = useState(false);

  return (
    <Style g={24} f={1}>
      <Heading type="h3">Create tag</Heading>
      <Flex a="center" g={8} f={1}>
        <ColorSelector
          value={state.color}
          onChange={(e) => setValue('color', e)}
        />
        <Input
          initFocus
          name="name"
          sizeType="m"
          placeholder="Type tag name"
          value={state.name}
          wrapperStyle={{ flex: 1 }}
          onChange={(e) => setValue('name', e.target.value)}
        />
      </Flex>
      <Flex g={8} j="flex-end">
        <Button
          sizeType="m"
          isLoading={isLoading}
          variant="secondary"
          onClick={closeModal}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          sizeType="m"
          isLoading={isLoading}
          onClick={async () => {
            if (!rootTagNode) return;
            const siblingNames = rootTagNode.children.map(
              (t) => tagMap[t].name,
            );
            const errorMessage = validateTagNameProperty(
              state.name,
              siblingNames,
            );
            if (errorMessage) {
              addToast(errorMessage, 'fail');
              return;
            }
            try {
              setIsLoading(true);
              await addTag(rootTagNode.alias, state.name, state.color, false);
              await closeModal();
              props.onCreateTagSuccess();
            } catch (error) {
              console.error(error);
              addToast('Failed to create tag', 'fail');
            } finally {
              setIsLoading(false);
            }
          }}
        >
          {isLoading ? 'Creating...' : 'Create'}
        </Button>
      </Flex>
    </Style>
  );
};

const Style = styled(VFlex)`
  padding: 24px;
  width: 400px;
  ${modalStyle};
`;

export default CreateTagModal;
